import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Loader2, ChevronLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const { language } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const sk = (s, c) => (language === 'sk' ? s : c);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Call regardless — endpoint always returns success
      await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
    } catch {
      // Same UX even on error — don't leak info
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center p-4" data-testid="forgot-submitted">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-hover border border-border/40 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-secondary" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-text-heading mb-3">
            {sk('Email odoslaný', 'E-mail odeslán')}
          </h1>
          <p className="text-sm text-muted-foreground mb-2">
            {sk(
              `Ak existuje účet s adresou ${email}, do niekoľkých minút dostanete email s odkazom na obnovu hesla.`,
              `Pokud existuje účet s adresou ${email}, během několika minut dostanete e-mail s odkazem na obnovu hesla.`
            )}
          </p>
          <p className="text-xs text-muted-foreground mb-6">
            {sk('Skontrolujte aj spam priečinok.', 'Zkontrolujte i spam složku.')}
          </p>
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="rounded-full px-8"
            data-testid="back-to-login-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {sk('Späť na prihlásenie', 'Zpět na přihlášení')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-subtle flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-hover border border-border/40 p-8">
        <div className="text-center mb-6">
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading mb-2">
            {sk('Zabudli ste heslo?', 'Zapomněli jste heslo?')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {sk('Zadajte vašu emailovú adresu a pošleme vám odkaz na obnovu hesla.',
                'Zadejte vaši e-mailovou adresu a pošleme vám odkaz na obnovu hesla.')}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vy@example.sk"
              className="pl-10 rounded-xl"
              required
              data-testid="forgot-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting || !email}
            className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl py-6"
            data-testid="forgot-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {sk('Odosielam…', 'Odesílám…')}
              </>
            ) : (
              sk('Odoslať odkaz na obnovu', 'Odeslat odkaz na obnovu')
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-3 h-3 inline mr-1" />
            {sk('Späť na prihlásenie', 'Zpět na přihlášení')}
          </button>
        </div>
      </div>
    </div>
  );
}
